import React, { useState } from 'react';
import VerticalNavigation from '@salesforce/design-system-react/components/vertical-navigation';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, elevation, motion } from './shared/tokens';

import DonorProfile from './pages/DonorProfile';
import HouseholdAccount from './pages/HouseholdAccount';
import RecurringDonation from './pages/RecurringDonation';
import RelationshipExplorer from './pages/RelationshipExplorer';

const pages = [
  { id: 'overview',    label: 'Overview',              icon: 'home',           component: null },
  { id: 'donor',       label: 'Donor Profile',         icon: 'contact',        component: DonorProfile,
    summary: 'Hero · engagement gauge · donor score · unified timeline · moves funnel · next-best-action', accent: '#7F8DE1' },
  { id: 'household',   label: 'Household Account',     icon: 'household',      component: HouseholdAccount,
    summary: 'Roster · contribution share · seasonal addresses · household rollups', accent: '#E27B7B' },
  { id: 'recurring',   label: 'Recurring Donation',    icon: 'recurring_donation', component: RecurringDonation,
    summary: '12-month schedule strip · MRR · pause / upgrade · lapse countdown', accent: '#2e844a' },
  { id: 'relationships', label: 'Relationship Explorer', icon: 'relationship',   component: RelationshipExplorer,
    summary: 'Dagre graph · household · affiliations · soft-credit network', accent: '#9050e9' },
];

const navCategories = [
  { id: 'npsp-components-nav',
    label: 'NPSP Components',
    items: pages.map(p => ({ id: p.id, label: p.label })),
  },
];

const OverviewPage = ({ onNavigate }) => (
  <div style={{ maxWidth: 980, margin: '0 auto', padding: space[8] }}>
    <div style={{ textAlign: 'center', marginBottom: space[9] }}>
      <h1 style={{ ...type.display, color: colors.textPrimary, margin: 0, marginBottom: space[3] }}>
        NPSP, reimagined.
      </h1>
      <p style={{ ...type.body, color: colors.textTertiary, maxWidth: 680, margin: '0 auto', lineHeight: 1.6 }}>
        A next-gen set of donor, household, recurring-giving, and relationship views
        for Salesforce NPSP. Built from competitive research across Bloomerang, Virtuous,
        Raiser&apos;s Edge NXT, Givebutter, and Fundraise Up.
      </p>
      <p style={{ ...type.small, color: colors.textTertiary, marginTop: space[3] }}>
        See <code style={{ background: colors.surfaceInk, padding: '2px 6px', borderRadius: radius.sm }}>
          docs/NPSP-COMPONENTS-BLUEPRINT.md
        </code> for the full spec.
      </p>
    </div>

    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: space[5],
    }}>
      {pages.filter(p => p.id !== 'overview').map(page => (
        <button
          key={page.id}
          onClick={() => onNavigate(page.id)}
          className={`npsp-walkthrough-${page.id}`}
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.lg,
            padding: space[6],
            cursor: 'pointer',
            textAlign: 'left',
            transition: `all ${motion.base}`,
            fontFamily: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            gap: space[3],
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = colors.brand;
            e.currentTarget.style.boxShadow = elevation.cardHover;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: space[3] }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: radius.md,
              background: page.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon category="standard" name={page.icon} size="small" style={{ fill: '#fff' }} />
            </div>
            <h3 style={{ ...type.h3, color: colors.textPrimary, margin: 0 }}>
              {page.label}
            </h3>
          </div>
          <p style={{ ...type.small, color: colors.textTertiary, margin: 0, lineHeight: 1.5 }}>
            {page.summary}
          </p>
        </button>
      ))}
    </div>
  </div>
);

const NPSPComponents = () => {
  const [activeId, setActiveId] = useState('overview');
  const ActiveComponent = pages.find(p => p.id === activeId)?.component;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: colors.surfaceInk }}>
      {/* Left rail */}
      <aside style={{
        width: 260,
        minWidth: 260,
        background: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ padding: `${space[4]}px ${space[5]}px`, borderBottom: `1px solid ${colors.border}` }}>
          <h2 style={{ ...type.h3, color: colors.textPrimary, margin: 0 }}>NPSP Components</h2>
          <p style={{ ...type.micro, color: colors.textTertiary, margin: `${space[1]}px 0 0 0` }}>
            Next-gen nonprofit UX
          </p>
        </div>
        <VerticalNavigation
          categories={navCategories}
          selectedId={activeId}
          onSelect={(e, data) => setActiveId(data.item.id)}
        />
      </aside>

      {/* Main content — bottom chrome (footer + dev banner) handled globally in App.js */}
      <main style={{ flex: 1, overflowY: 'auto' }}>
        {activeId === 'overview'
          ? <OverviewPage onNavigate={setActiveId} />
          : ActiveComponent
            ? <ActiveComponent />
            : <div style={{ padding: space[8], color: colors.textTertiary }}>Not found.</div>
        }
      </main>
    </div>
  );
};

export default NPSPComponents;
